import axios from 'axios';
import { toast } from 'react-hot-toast';
import apiConfig from 'src/configs/auth';

const API_BASE_URL = apiConfig.BASE_URL;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Refresh token function
export const refreshToken = async () => {
  const storedRefreshToken = localStorage.getItem('refreshToken');
  if (!storedRefreshToken) {
    throw new Error('No refresh token available');
  }

  try {
    const response = await axios.post(`${API_BASE_URL}/api/token/refresh/`, {
      refresh: storedRefreshToken,
    });

    if (response.data.access) {
      localStorage.setItem('accessToken', response.data.access);
      return response.data.access;
    }
    throw new Error('No access token in response');
  } catch (error) {
    throw error;
  }
};

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        await refreshToken();
        if (typeof window !== 'undefined') {
          const token = localStorage.getItem('accessToken');
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
        }
        return api(originalRequest);
      } catch (refreshError) {
        if (typeof window !== 'undefined') {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('userData');
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// Auth API

export const login = async (credentials) => {
  try {
    const response = await api.post('/api/login/', credentials);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getMe = async () => {
  try {
    const response = await api.get('/api/auth_me/');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const requestPasswordReset = async (email) => {
  try {
    const response = await api.post('/api/password_reset/', { email });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const confirmPasswordReset = async (data) => {
  try {
    const response = await api.post('/api/password_reset_confirm/', data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Contributors API

export const getContributors = async () => {
  try {
    const response = await api.get('/api/contributors/');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const submitCampaign = async (campaignData) => {
  try {
    // Format phone number to remove any non-digit characters
    const formattedData = {
      ...campaignData,
      contactNumber: campaignData.contactNumber.replace(/\D/g, ''),
    };

    const response = await api.post('/api/public-campaigns/', formattedData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get all public campaigns/organizations
export const getOrganizations = async () => {
  try {
    const response = await api.get('/api/public-campaigns/');
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Update organization approval status
export const updateOrganizationApproval = async (id, approve) => {
  try {
    const response = await api.patch(`/api/public-campaigns/${id}/`, {
      approved: approve,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Add this function to fetch home hero campaign statistics
// THIS SHOULD BE STATIC DATA COMPUTED AT BUILD TIME
export const getHomeHeroCampaignStatistics = async () => {
  try {
    const response = await api.get('/api/home-hero-campaign-statistics');
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Add this function to handle contact form submissions
export const submitContactForm = async (contactData) => {
  try {
    const response = await api.post('/api/contact/', contactData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Add newsletter subscription function
export const subscribeToNewsletter = async (email) => {
  try {
    const response = await api.post('/api/newsletter/subscribe/', { email });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Add these API functions
export const getContactMessages = async () => {
  try {
    const response = await api.get('/api/contact/messages/');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteContactMessage = async (id) => {
  try {
    await api.delete(`/api/contact/messages/${id}/`);
  } catch (error) {
    throw error;
  }
};

// Add this function for sending replies
export const sendContactMessageReply = async (messageId, replyText) => {
  try {
    const response = await api.post(
      `/api/contact/messages/${messageId}/reply/`,
      {
        reply_text: replyText,
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Campaign API functions

export const getCampaigns = async () => {
  try {
    const response = await api.get('/api/campaigns/');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCampaignDetails = async (id, options = {}) => {
  const {
    enableRetry = true,
    maxRetries = 3,
    retryDelay = 1000,
    showErrorToast = true
  } = options;

  const apiCall = async () => {
    try {
      const response = await api.get(`/api/campaign-details/${id}/`, {
        timeout: 8000
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Campaign details API failed:', error.message);
      throw error;
    }
  };

  // Retry with exponential backoff
  const retryWithBackoff = async (attempt = 1) => {
    try {
      return await apiCall();
    } catch (error) {
      if (attempt >= maxRetries || !enableRetry) {
        const errorResult = {
          success: false,
          error: 'Unable to load campaign details. Please try again.',
          technicalError: error.message
        };

        if (showErrorToast) {
          toast.error('Failed to load campaign details');
        }

        throw errorResult;
      }

      const isRetryable = error.code === 'NETWORK_ERROR' ||
        error.code === 'ECONNABORTED' ||
        (error.response && error.response.status >= 500);

      if (!isRetryable) {
        throw error;
      }

      const delay = retryDelay * Math.pow(2, attempt - 1);
      console.info(`Retrying campaign details in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`);

      await new Promise(resolve => setTimeout(resolve, delay));
      return retryWithBackoff(attempt + 1);
    }
  };

  return retryWithBackoff();
};


export const getCampaignBySlug = async (slug, options = {}) => {
  const {
    enableRetry = true,
    maxRetries = 3,
    retryDelay = 1000,
    showErrorToast = true
  } = options;

  const apiCall = async () => {
    try {
      const response = await api.get('/api/campaign_details_by_slug', {
        params: { slug },
        timeout: 8000
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Campaign by slug API failed:', error.message);
      throw error;
    }
  };

  // Retry with exponential backoff
  const retryWithBackoff = async (attempt = 1) => {
    try {
      return await apiCall();
    } catch (error) {
      if (attempt >= maxRetries || !enableRetry) {
        const errorResult = {
          success: false,
          error: 'Campaign not found or unable to load. Please check the URL and try again.',
          technicalError: error.message,
          isNotFound: error.response && error.response.status === 404
        };

        if (showErrorToast) {
          const message = error.response && error.response.status === 404
            ? 'Campaign not found'
            : 'Failed to load campaign';
          toast.error(message);
        }

        throw errorResult;
      }

      // Don't retry 404 errors
      if (error.response && error.response.status === 404) {
        throw error;
      }

      const isRetryable = error.code === 'NETWORK_ERROR' ||
        error.code === 'ECONNABORTED' ||
        (error.response && error.response.status >= 500);

      if (!isRetryable) {
        throw error;
      }

      const delay = retryDelay * Math.pow(2, attempt - 1);
      console.info(`Retrying campaign by slug in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`);

      await new Promise(resolve => setTimeout(resolve, delay));
      return retryWithBackoff(attempt + 1);
    }
  };

  return retryWithBackoff();
};

// Unified campaign details update (PATCH). Supports FormData or JSON.
export const updateCampaignDetails = async (id, data, options = {}) => {
  if (!id) throw new Error('updateCampaignDetails: id is required');
  const {
    enableRetry = true,
    maxRetries = 2,
    retryDelay = 2000,
    showErrorToast = true
  } = options;
  const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;

  const apiCall = async () => {
    try {
      const response = await api.patch(`/api/campaign-details/${id}/`, data, {
        headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : undefined,
        timeout: 15000
      });
      return { success: true, data: response.data };
    } catch (error) {
      // Validation errors: surface and don't retry
      if (error.response && error.response.status >= 400 && error.response.status < 500) {
        if (showErrorToast) {
          const msg = error.response.data?.message || 'Invalid campaign data';
          toast.error(msg);
        }
        // Wrap validation meta in Error instance for lint compliance
        const validationErr = new Error('Validation failed');
        validationErr.isValidationError = true;
        validationErr.original = error;
        throw validationErr;
      }
      throw error;
    }
  };

  const retryWithBackoff = async (attempt = 1) => {
    try {
      return await apiCall();
    } catch (err) {
      if (err.isValidationError) {
        if (err.original) throw err.original;
        throw err;
      }
      if (attempt >= maxRetries || !enableRetry) throw (err instanceof Error ? err : new Error(err.message || 'Campaign update failed'));
      const isRetryable = err.code === 'NETWORK_ERROR' || err.code === 'ECONNABORTED' || (err.response && err.response.status >= 500);
      if (!isRetryable) throw err;
      const delay = retryDelay * Math.pow(2, attempt - 1);
      await new Promise(r => setTimeout(r, delay));
      return retryWithBackoff(attempt + 1);
    }
  };

  return retryWithBackoff();
};

export const getCampaignStats = async (campaignId, options = {}) => {
  const {
    enableRetry = true,
    enableFallback = true,
    showErrorToast = true,
    maxRetries = 3,
    retryDelay = 1000
  } = options;

  // Primary API call with enhanced error handling
  const primaryCall = async () => {
    try {
      const response = await api.get(`/api/campaign-stats/${campaignId}/`, {
        timeout: 10000 // 10 second timeout
      });
      return {
        success: true,
        data: response.data,
        source: 'campaign-stats'
      };
    } catch (error) {
      console.error('Campaign stats API failed:', error.message);
      throw error;
    }
  };

  // Fallback to campaign dashboard API
  const fallbackCall = async () => {
    try {
      const response = await api.get(`/api/campaign-dashboard/${campaignId}`, {
        timeout: 8000 // Shorter timeout for fallback
      });
      const data = response.data;

      // Transform dashboard data to stats format
      return {
        success: true,
        data: {
          total_collected: data.total_collected || 0,
          donor_number: data.donor_number || 0,
          total_team_number: data.total_team_number || 0,
          total_individual_donate: data.total_individual_donate || 0,
          total_team_donate_amount: data.total_team_donate_amount || 0,
          campaign_goal: data.campaign_goal || 0
        },
        source: 'campaign-dashboard',
        isFallback: true
      };
    } catch (error) {
      console.error('Campaign dashboard fallback failed:', error.message);
      throw error;
    }
  };

  // Second fallback to campaign details
  const secondaryFallbackCall = async () => {
    try {
      const response = await api.get(`/api/campaign-details/${campaignId}/`, {
        timeout: 6000
      });
      const data = response.data;

      return {
        success: true,
        data: {
          total_collected: data.collected_amount || 0,
          donor_number: 0, // Not available in details
          total_team_number: 0,
          total_individual_donate: 0,
          total_team_donate_amount: 0,
          campaign_goal: data.goal || 0
        },
        source: 'campaign-details',
        isFallback: true,
        isLimitedData: true
      };
    } catch (error) {
      console.error('Campaign details fallback failed:', error.message);
      throw error;
    }
  };

  // Retry logic with exponential backoff
  const retryWithBackoff = async (apiCall, attempt = 1) => {
    try {
      return await apiCall();
    } catch (error) {
      if (attempt >= maxRetries || !enableRetry) {
        throw error;
      }

      // Check if error is retryable
      const isRetryable = error.code === 'NETWORK_ERROR' ||
        error.code === 'ECONNABORTED' ||
        (error.response && error.response.status >= 500);

      if (!isRetryable) {
        throw error;
      }

      const delay = retryDelay * Math.pow(2, attempt - 1); // Exponential backoff
      console.info(`Retrying API call in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`);

      await new Promise(resolve => setTimeout(resolve, delay));
      return retryWithBackoff(apiCall, attempt + 1);
    }
  };

  try {
    // Try primary API with retry
    return await retryWithBackoff(primaryCall);
  } catch (primaryError) {
    console.warn('Primary campaign stats API failed, trying fallbacks...');

    if (enableFallback) {
      try {
        // Try first fallback
        return await retryWithBackoff(fallbackCall);
      } catch (fallbackError) {
        console.warn('First fallback failed, trying secondary fallback...');

        try {
          // Try secondary fallback
          return await retryWithBackoff(secondaryFallbackCall);
        } catch (secondaryError) {
          console.error('All campaign stats endpoints failed');

          // Return error with user-friendly message
          const errorResult = {
            success: false,
            error: 'Unable to load campaign statistics. Please check your connection and try again.',
            technicalError: primaryError.message,
            allEndpointsFailed: true
          };

          if (showErrorToast) {
            toast.error('Failed to load campaign statistics');
          }

          throw errorResult;
        }
      }
    } else {
      throw primaryError;
    }
  }
};

export const getCampaignTeams = async (campaignId, options = {}) => {
  const {
    enableRetry = true,
    maxRetries = 3,
    retryDelay = 1000,
    showErrorToast = true
  } = options;

  const apiCall = async () => {
    try {
      const response = await api.get('/api/campaign_teams/', {
        params: { campaign_id: campaignId },
        timeout: 8000
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Campaign teams API failed:', error.message);
      throw error;
    }
  };

  // Retry logic
  const retryWithBackoff = async (attempt = 1) => {
    try {
      return await apiCall();
    } catch (error) {
      if (attempt >= maxRetries || !enableRetry) {
        const errorResult = {
          success: false,
          error: 'Unable to load team information. Please try again.',
          technicalError: error.message
        };

        if (showErrorToast) {
          toast.error('Failed to load team information');
        }

        throw errorResult;
      }

      // Check if error is retryable
      const isRetryable = error.code === 'NETWORK_ERROR' ||
        error.code === 'ECONNABORTED' ||
        (error.response && error.response.status >= 500);

      if (!isRetryable) {
        throw error;
      }

      const delay = retryDelay * Math.pow(2, attempt - 1);
      console.info(`Retrying teams API call in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`);

      await new Promise(resolve => setTimeout(resolve, delay));
      return retryWithBackoff(attempt + 1);
    }
  };

  return retryWithBackoff();
};

export const createCampaignTeam = async (teamData, options = {}) => {
  const {
    enableRetry = true,
    maxRetries = 2, // Fewer retries for create operations
    retryDelay = 2000,
    showErrorToast = true
  } = options;

  const apiCall = async () => {
    try {
      const response = await api.post('/api/campaign_teams/', teamData, {
        timeout: 10000
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Create campaign team failed:', error.message);

      // Don't retry certain errors (like validation errors)
      if (error.response && error.response.status >= 400 && error.response.status < 500) {
        const validationError = {
          success: false,
          error: error.response.data?.message || 'Invalid team data. Please check your input.',
          isValidationError: true,
          validationDetails: error.response.data
        };
        throw validationError;
      }

      throw error;
    }
  };

  // Retry logic for network/server errors only
  const retryWithBackoff = async (attempt = 1) => {
    try {
      return await apiCall();
    } catch (error) {
      // Don't retry validation errors
      if (error.isValidationError) {
        if (showErrorToast) {
          toast.error(error.error);
        }
        throw error;
      }

      if (attempt >= maxRetries || !enableRetry) {
        const errorResult = {
          success: false,
          error: 'Unable to create team. Please try again.',
          technicalError: error.message
        };

        if (showErrorToast) {
          toast.error('Failed to create team');
        }

        throw errorResult;
      }

      // Only retry network/server errors
      const isRetryable = error.code === 'NETWORK_ERROR' ||
        error.code === 'ECONNABORTED' ||
        (error.response && error.response.status >= 500);

      if (!isRetryable) {
        throw error;
      }

      const delay = retryDelay * Math.pow(2, attempt - 1);
      console.info(`Retrying create team in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`);

      await new Promise(resolve => setTimeout(resolve, delay));
      return retryWithBackoff(attempt + 1);
    }
  };

  return retryWithBackoff();
};

export const getTeamBySlug = async (slug) => {
  try {
    const response = await api.get('/api/team_details_by_slug/', {
      params: { slug },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const uploadBannerImage = async (formData) => {
  try {
    const response = await api.post('/api/banner_image/', formData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getBannerImages = async (campaignDetailsId) => {
  try {
    const response = await api.get(
      `/api/banner_image?campaign_details_id=${campaignDetailsId}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const uploadBannerVideo = async (formData) => {
  try {
    const response = await api.post('/api/banner_video/', formData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCampaignSEO = async (campaignId) => {
  try {
    const response = await api.get('/api/campaign-seo/', {
      params: { campaign_id: campaignId },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateCampaignSEO = async (seoData) => {
  try {
    const response = await api.put('/api/campaign-seo/', seoData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCampaignDonors = async (campaignId) => {
  try {
    const response = await api.get(`/api/campaigns/${campaignId}/donors`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getPublicCampaigns = async () => {
  try {
    const response = await api.get('/api/priority-banner-image');
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Donation API functions

export const processDonation = async (donationData) => {
  try {
    const response = await api.post('/api/donate/', donationData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const processAnonymousDonation = async (donationData) => {
  try {
    const response = await api.post('/api/anonymous/', donationData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCampaignDonations = async (campaignId) => {
  try {
    const response = await api.get(`/api/donations_by_campaign/${campaignId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getTeamDonations = async (campaignId, teamId) => {
  try {
    const response = await api.get(
      `/api/campaign_team_wise_donations/campaign/${campaignId}/team/${teamId}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const saveCardInfo = async (cardData) => {
  try {
    const response = await api.post('/api/save_card_info/', cardData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getDonorSavedCards = async (donorId) => {
  try {
    const response = await api.get('/api/donor_save_card_list/', {
      params: { donor_id: donorId },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getTransactionDetails = async (transactionId) => {
  try {
    const response = await api.get('/api/transactions_details/', {
      params: { transaction_id: transactionId },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getLastFiveTransactions = async (campaignId) => {
  try {
    const response = await api.get('/api/last-five-transactions/', {
      params: { campaign_id: campaignId },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteDonation = async (donationId) => {
  try {
    const response = await api.delete('/api/delete-donation/', {
      data: { donation_id: donationId },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getDonorInstallmentInfo = async (donorId) => {
  try {
    const response = await api.get('/api/donor_installment_info/', {
      params: { donor_id: donorId },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Gateway API functions

export const getGateways = async () => {
  try {
    const response = await api.get('/api/gateways/');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCampaignGateways = async (campaignSlug) => {
  try {
    const response = await api.get(
      `/api/gateways/campaign-gateways/?campaign_slug=${campaignSlug}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const configureGateway = async (gatewayData) => {
  try {
    const response = await api.post('/api/gateways/configure/', gatewayData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createStripeCheckoutSession = async (sessionData) => {
  try {
    const response = await api.post(
      '/api/create-checkout-session-stripe/',
      sessionData
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createStripePaymentLink = async (linkData) => {
  try {
    const response = await api.post('/api/create_payment_link/', linkData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const validateApplePay = async (validationData) => {
  try {
    const response = await api.post('/api/apple_pay_validate/', validationData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const addPayment = async (paymentData) => {
  try {
    const response = await api.post('/api/add_payment/', paymentData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const switchGateway = async (switchData) => {
  try {
    const response = await api.put('/api/gateways/switch/', switchData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getGatewayHealth = async () => {
  try {
    const response = await api.get('/api/gateways/health/');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export default api;
